import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { RACK_TYPE_SHORT, type StockResponse, type StockRow, type StockStatus } from '@reikraft/shared';
import { type Column, Dot, ErrorBox, Icon, Kpi, Kpis, Loader, Notice, SearchBox, Switch, Table, Tabs, useToast } from '@reikraft/ui';
import { api, errorText, qs } from '../api';
import { PageHead } from '../components/common';
import { formatMoney } from '../format';
import { useDebounced, useIsMobile, useUrlState } from '../hooks';

const STATUS_LABEL: Record<StockStatus, string> = {
  pub: 'НА ВИТРИНЕ',
  draft: 'ЧЕРНОВИК',
  res: 'РЕЗЕРВ',
  sold: 'ПРОДАНО',
  archived: 'АРХИВ',
};
const STATUS_COLOR: Record<StockStatus, string> = {
  pub: '#5BD16B',
  draft: '#868B93',
  res: '#F2C230',
  sold: '#4A4F57',
  archived: '#4A4F57',
};

export function StockPage() {
  const [state, set] = useUrlState({ tab: 'all' });
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const isMobile = useIsMobile();
  const stock = useQuery({ queryKey: ['stock', dq], queryFn: () => api.get<StockResponse>(`/admin/stock${qs({ q: dq })}`) });
  const toggle = useMutation({
    mutationFn: (r: StockRow) => api.post(`/admin/products/${r.productId}/${r.status === 'pub' ? 'unpublish' : 'publish'}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock'] }),
    onError: (e) => toast(errorText(e), { error: true }),
  });

  const d = stock.data;
  const rows = (d?.rows ?? []).filter((r) => (state.tab === 'all' ? r.status !== 'archived' : r.status === state.tab));
  const count = (s: StockStatus) => (d?.rows ?? []).filter((r) => r.status === s).length;
  const tabs = [
    { id: 'all', label: 'Все', count: (d?.rows ?? []).filter((r) => r.status !== 'archived').length },
    { id: 'pub', label: 'На витрине', count: count('pub') },
    { id: 'draft', label: 'Черновики', count: count('draft') },
    { id: 'res', label: 'Резерв', count: count('res') },
    { id: 'sold', label: 'Продано', count: count('sold') },
    ...(count('archived') ? [{ id: 'archived', label: 'Архив', count: count('archived') }] : []),
  ];

  const columns: Column<StockRow>[] = [
    {
      key: 'photo',
      title: 'Фото',
      width: '52px',
      render: (r) => (
        <span
          style={{
            width: 52,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--panel-2)',
            border: '1px solid var(--line)',
            overflow: 'hidden',
          }}
        >
          {r.cover ? (
            <img src={r.cover.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span className="rk-mono rk-muted-2" style={{ fontSize: 10 }}>
              {r.photos || '—'}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'sku',
      title: 'Артикул',
      width: '96px',
      render: (r) => (
        <span className="rk-td--stack">
          <b className="rk-mono">{r.sku}</b>
          <span className="rk-td__mono-sub">{RACK_TYPE_SHORT[r.rackType]}</span>
        </span>
      ),
    },
    {
      key: 'car',
      title: 'Рейка',
      width: 'minmax(180px, 1.3fr)',
      render: (r) => (
        <span className="rk-td--stack">
          <b style={{ fontWeight: 600 }}>{r.car}</b>
          <span className="rk-td__sub" style={{ color: r.warning && r.status === 'draft' ? 'var(--danger-text)' : undefined }}>
            {r.warning ?? [r.years, `${r.photos} фото`].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      key: 'src',
      title: 'Источник',
      width: 'minmax(140px, 1fr)',
      render: (r) => (
        <span className="rk-td--stack">
          <span>{r.sources}</span>
          <span className="rk-td__mono-sub">{r.sourceRef ?? ''}</span>
        </span>
      ),
    },
    {
      key: 'bench',
      title: 'Стенд',
      width: '120px',
      render: (r) => (
        <span className="rk-row rk-mono" style={{ ['--rk-gap' as string]: '6px', fontSize: 11 }}>
          <Dot color={r.benchOk ? 'var(--success)' : 'var(--muted-2)'} size="sm" />
          {r.benchLabel}
        </span>
      ),
    },
    {
      key: 'price',
      title: 'Цена / обмен',
      width: '120px',
      render: (r) => (
        <span className="rk-td--stack rk-mono">
          <b>{formatMoney(r.priceKop)}</b>
          <span className="rk-td__mono-sub">{formatMoney(r.exchangePriceKop)}</span>
        </span>
      ),
    },
    {
      key: 'qty',
      title: 'Шт',
      width: '56px',
      render: (r) => (
        <b className="rk-mono">
          {r.qty}
          {r.reserved ? <span className="rk-muted"> +{r.reserved}р</span> : ''}
        </b>
      ),
    },
    { key: 'place', title: 'Место', width: '72px', render: (r) => <span className="rk-mono rk-text-2">{r.places}</span> },
    {
      key: 'pub',
      title: 'Витрина',
      width: '130px',
      render: (r) =>
        r.status === 'pub' || r.status === 'draft' ? (
          <span onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={r.status === 'pub'}
              onChange={() => toggle.mutate(r)}
              label={`Показывать на витрине: ${r.sku}`}
              showLabel={STATUS_LABEL[r.status]}
              disabled={toggle.isPending}
            />
          </span>
        ) : (
          <span className="rk-badge">
            <Dot color={STATUS_COLOR[r.status]} size="sm" />
            {STATUS_LABEL[r.status]}
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHead
        title="Склад и витрина"
        actions={
          <Link to="/stock/new" className="rk-btn rk-btn--primary">
            <Icon name="plus" size={16} /> Выставить на продажу
          </Link>
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder="Артикул, модель, OEM, место" label="Поиск по складу" />
      </PageHead>

      {d && d.awaiting.length > 0 && (
        <Notice
          tone="stand"
          label={<span style={{ color: 'var(--stand-text)' }}>После стенда</span>}
          dot="var(--stand)"
          actions={
            <Link to={`/stock/new?unit=${d.awaiting[0].unitId}`} className="rk-btn rk-btn--stand">
              Оформить →
            </Link>
          }
        >
          {d.awaiting.length} {d.awaiting.length === 1 ? 'рейка из обменного фонда прошла' : 'реек из обменного фонда прошли'} испытания и
          {d.awaiting.length === 1 ? ' ждёт' : ' ждут'} оформления:{' '}
          {d.awaiting.map((a, i) => (
            <span key={a.unitId}>
              {i > 0 && ', '}
              <Link to={`/stock/new?unit=${a.unitId}`}>
                <b className="rk-mono">{a.number}</b> {a.car}
              </Link>
            </span>
          ))}
        </Notice>
      )}

      {d && (
        <Kpis min={isMobile ? 130 : 160}>
          <Kpi label="На витрине" value={d.kpi.published} dot="#5BD16B" />
          <Kpi label="Черновики" value={d.kpi.drafts} dot="#868B93" />
          <Kpi label="В резерве" value={d.kpi.reserved} dot="#F2C230" />
          <Kpi label={`Продано · ${d.kpi.monthName}`} value={d.kpi.soldMonth} />
          <Kpi label="Единиц на складе" value={d.kpi.units} />
        </Kpis>
      )}

      <Tabs items={tabs} value={state.tab} onChange={(tab) => set({ tab })} label="Статус позиции" />

      {stock.isLoading ? (
        <Loader />
      ) : stock.isError ? (
        <ErrorBox error={stock.error} onRetry={() => stock.refetch()} />
      ) : (
        <Table
          label="Склад"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.productId}
          onRowClick={(r) => navigate(`/stock/${r.productId}`)}
          minWidth={1120}
          empty="Позиций нет. Выставьте на продажу рейку после стенда или выкупленную партию."
        />
      )}
    </>
  );
}
